import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { useTranslation } from '@repo/i18n'
import { ChevronLeft, Menu, X } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useContext,
	useId,
	useState
} from 'react'
import { useHoverEffect } from '#aceternity/card-hover-effect'
import { Button } from '#components/button'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetTitle,
	SheetTrigger
} from '#components/sheet'
import { useIsMobile } from '#hooks/use-mobile'
import { cn } from '#lib/utils'

/*
 * Aceternity collapsible sidebar, adapted:
 * - an explicit arrow toggles the desktop width and moves the page beside it;
 * - the mobile sheet has its own state, so it cannot reset the desktop choice;
 * - theme tokens instead of fixed neutrals, lucide instead of tabler icons;
 * - links render through `render` (e.g. a router <Link>), not a bare <a href>;
 * - inside a HoverEffectGroup the hover background slides between links.
 */

const RAIL_WIDTH = 60
const OPEN_WIDTH = 200

const SidebarContext = createContext<
	| {
			open: boolean
			setOpen: Dispatch<SetStateAction<boolean>>
			animate: boolean
	  }
	| undefined
>(undefined)

export function useSidebar() {
	const context = useContext(SidebarContext)
	if (!context) {
		throw new Error('useSidebar must be used within a Sidebar')
	}
	return context
}

export function Sidebar(props: {
	children: ReactNode
	open?: boolean
	setOpen?: Dispatch<SetStateAction<boolean>>
	animate?: boolean
}) {
	const [openState, setOpenState] = useState(false)

	return (
		<SidebarContext.Provider
			value={{
				open: props.open ?? openState,
				setOpen: props.setOpen ?? setOpenState,
				animate: props.animate ?? true
			}}
		>
			{props.children}
		</SidebarContext.Provider>
	)
}

export function SidebarBody(props: {
	className?: string
	children: ReactNode
}) {
	const isMobile = useIsMobile()
	return isMobile ? (
		<MobileSidebar className={props.className}>
			{props.children}
		</MobileSidebar>
	) : (
		<DesktopSidebar className={props.className}>
			{props.children}
		</DesktopSidebar>
	)
}

function DesktopSidebar(props: { className?: string; children: ReactNode }) {
	const sidebar = useSidebar()
	const { t } = useTranslation()
	const reduceMotion = useReducedMotion()
	const contentId = useId()
	const expanded = !sidebar.animate || sidebar.open

	return (
		<motion.aside
			animate={{ width: expanded ? OPEN_WIDTH : RAIL_WIDTH }}
			aria-label={t('shared:sidebar.navigation')}
			className='group/sidebar relative my-2 hidden shrink-0 rounded-xl bg-sidebar text-sidebar-foreground md:flex'
			data-state={expanded ? 'open' : 'closed'}
			initial={false}
			transition={
				reduceMotion
					? { duration: 0 }
					: { duration: 0.2, ease: [0.2, 0, 0, 1] }
			}
		>
			<div
				className={cn(
					'flex min-w-0 flex-1 flex-col overflow-hidden px-2 py-1',
					props.className
				)}
				id={contentId}
			>
				{props.children}
			</div>
			{sidebar.animate && (
				<Button
					aria-controls={contentId}
					aria-expanded={expanded}
					aria-label={
						expanded
							? t('shared:sidebar.collapse')
							: t('shared:sidebar.expand')
					}
					className='absolute top-2.5 left-full z-10 ml-2 -translate-x-1/2 rounded-full border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent'
					onClick={() => {
						sidebar.setOpen((open) => !open)
					}}
					size='icon-xs'
					variant='outline'
				>
					<ChevronLeft
						aria-hidden='true'
						className={cn(
							'size-3.5 transition-transform duration-200 motion-reduce:transition-none',
							!expanded && 'rotate-180'
						)}
					/>
				</Button>
			)}
		</motion.aside>
	)
}

function MobileSidebar(props: { className?: string; children: ReactNode }) {
	const [open, setOpen] = useState(false)
	const { t } = useTranslation()

	return (
		<SidebarContext.Provider value={{ open, setOpen, animate: false }}>
			<Sheet onOpenChange={setOpen} open={open}>
				<div className='flex h-12 w-full shrink-0 items-center justify-end bg-sidebar px-3 md:hidden'>
					<SheetTrigger
						aria-label={t('shared:sidebar.open')}
						render={<Button size='icon' variant='ghost' />}
					>
						<Menu aria-hidden='true' className='size-5' />
					</SheetTrigger>
				</div>
				<SheetContent
					className={cn(
						'group/sidebar bg-sidebar p-6 text-sidebar-foreground data-[side=left]:w-full motion-reduce:transition-none data-[side=left]:sm:max-w-none',
						props.className
					)}
					data-state='open'
					showCloseButton={false}
					side='left'
				>
					<SheetTitle className='sr-only'>
						{t('shared:sidebar.navigation')}
					</SheetTitle>
					<SheetClose
						aria-label={t('shared:sidebar.close')}
						render={
							<Button
								className='absolute top-5 right-5'
								size='icon'
								variant='ghost'
							/>
						}
					>
						<X aria-hidden='true' className='size-5' />
					</SheetClose>
					{props.children}
				</SheetContent>
			</Sheet>
		</SidebarContext.Provider>
	)
}

export function SidebarLink(
	props: useRender.ComponentProps<'a'> & {
		icon: ReactNode
		label: string
	}
) {
	const { open, animate } = useSidebar()
	const reduceMotion = useReducedMotion()
	const { icon, label, render, className, ...rest } = props
	// The current page already sits on its own sheet: no highlight over it.
	const hover = useHoverEffect(
		label,
		'bg-sidebar-accent group-data-[status=active]/sidebar-link:hidden'
	)

	return useRender({
		defaultTagName: 'a',
		render,
		props: mergeProps<'a'>(
			{
				className: cn(
					'group/sidebar-link relative isolate flex h-9 shrink-0 items-center gap-3 rounded-md px-3.25 text-sm transition-colors hover:text-sidebar-accent-foreground focus-visible:outline-2 focus-visible:outline-sidebar-ring [&_svg]:size-[18px] [&_svg]:shrink-0',
					!hover && 'hover:bg-sidebar-accent',
					className
				),
				onMouseEnter: hover?.onMouseEnter,
				children: (
					<>
						{hover?.highlight}
						{icon}
						<motion.span
							animate={{ opacity: !animate || open ? 1 : 0 }}
							className='whitespace-nowrap'
							initial={false}
							transition={{ duration: reduceMotion ? 0 : 0.15 }}
						>
							{label}
						</motion.span>
					</>
				)
			},
			rest
		)
	})
}
